import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Minus, Plus, RotateCcw, Scale } from 'lucide-react';
import { Fragment, useState } from 'react';

import {
  MAX_COEFFICIENT,
  MIN_COEFFICIENT,
  PROCESS_EQUATIONS,
  PROCESS_LABELS,
  checkProcessEquation,
  startingCoefficients,
  type EquationCheckResult,
  type EquationSpecies,
  type ProcessEquationCase,
} from './combustionCorrosion';
import { AtomTally, FeedbackPanel, Formula } from './CombustionCorrosionVisuals';

function CoefficientStepper({
  species,
  value,
  onChange,
}: {
  species: EquationSpecies;
  value: number;
  onChange: (value: number) => void;
}) {
  const name = `${species.name} (${species.formula})`;
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl border border-hairline bg-surface px-2 py-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`Decrease coefficient for ${name}`}
          disabled={value <= MIN_COEFFICIENT}
          onClick={() => onChange(value - 1)}
          className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-40"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <span className="min-w-[1.5rem] text-center text-lg font-extrabold text-brand-coral" aria-label={`Coefficient for ${name}: ${value}`}>
          {value}
        </span>
        <button
          type="button"
          aria-label={`Increase coefficient for ${name}`}
          disabled={value >= MAX_COEFFICIENT}
          onClick={() => onChange(value + 1)}
          className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <span className="text-base font-extrabold text-ink">
        <Formula formula={species.formula} />
        <span className="text-xs font-bold text-slate2">({species.state})</span>
      </span>
      <span className="text-[11px] font-bold text-slate2">{species.name}</span>
    </div>
  );
}

function SideSteppers({
  species,
  offset,
  coefficients,
  onChange,
}: {
  species: EquationSpecies[];
  offset: number;
  coefficients: number[];
  onChange: (index: number, value: number) => void;
}) {
  return (
    <>
      {species.map((entry, index) => (
        <Fragment key={`${entry.formula}-${index}`}>
          {index > 0 ? <span className="self-center text-lg font-extrabold text-slate2">+</span> : null}
          <CoefficientStepper
            species={entry}
            value={coefficients[offset + index]}
            onChange={(value) => onChange(offset + index, value)}
          />
        </Fragment>
      ))}
    </>
  );
}

const STATUS_TITLES: Record<EquationCheckResult['status'], string> = {
  balanced: 'Balanced in the simplest whole-number ratio',
  'not-simplest': 'Balanced, but not in the simplest ratio',
  unbalanced: 'Not balanced yet',
  invalid: 'Check the coefficients',
};

export function ProcessEquationsMode() {
  const [entry, setEntry] = useState<ProcessEquationCase>(PROCESS_EQUATIONS[0]);
  const [coefficients, setCoefficients] = useState<number[]>(() => startingCoefficients(PROCESS_EQUATIONS[0]));
  useScienceTutorSection('ProcessEquationsMode', { entry: tutorSelection(entry), coefficients });
  const [result, setResult] = useState<EquationCheckResult | null>(null);

  const choose = (next: ProcessEquationCase) => {
    setEntry(next);
    setCoefficients(startingCoefficients(next));
    setResult(null);
  };
  const setCoefficient = (index: number, value: number) => {
    setCoefficients((current) => current.map((item, itemIndex) => (itemIndex === index ? value : item)));
    setResult(null);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <aside className="rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Choose a reaction</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-1" aria-label="Reactions to balance">
          {PROCESS_EQUATIONS.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === entry.id}
              onClick={() => choose(candidate)}
              className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                candidate.id === entry.id
                  ? 'border-brand-sky bg-wash-sky text-ink'
                  : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {candidate.title}
              <span className="mt-0.5 block text-xs font-bold text-slate2">{PROCESS_LABELS[candidate.process]}</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <h3 className="text-xl font-extrabold text-ink">{entry.title}</h3>
        <p className="mt-2 text-sm font-extrabold uppercase tracking-[0.1em] text-slate2">Word equation</p>
        <p className="mt-1 font-bold text-ink">{entry.wordEquation}</p>
        <p className="mt-4 text-sm font-extrabold uppercase tracking-[0.1em] text-slate2">
          Formula equation — set the coefficients
        </p>
        <div className="mt-2 flex flex-wrap items-stretch gap-2" aria-label="Equation coefficients">
          <SideSteppers species={entry.reactants} offset={0} coefficients={coefficients} onChange={setCoefficient} />
          <span className="flex flex-col items-center justify-center px-1 text-xl font-extrabold text-ink">
            {entry.arrowNote ? <span className="text-[10px] font-bold text-slate2">{entry.arrowNote}</span> : null}→
          </span>
          <SideSteppers
            species={entry.products}
            offset={entry.reactants.length}
            coefficients={coefficients}
            onChange={setCoefficient}
          />
        </div>
        {entry.boundary ? (
          <p className="mt-3 rounded-2xl border border-brand-sunshine/40 bg-wash-sunshine px-4 py-3 text-sm font-medium leading-6 text-ink-soft">
            <strong className="text-ink">Simplification:</strong> {entry.boundary}
          </p>
        ) : null}
        <p className="mt-3 text-xs font-medium leading-5 text-slate2">
          Change coefficients only. Changing a subscript would make a different substance.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setResult(checkProcessEquation(entry, coefficients))}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white"
          >
            <Scale className="h-4 w-4" aria-hidden="true" /> Check balance
          </button>
          <button
            type="button"
            onClick={() => choose(entry)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
          </button>
        </div>

        {result ? (
          <FeedbackPanel correct={result.status === 'balanced'} title={STATUS_TITLES[result.status]}>
            <p>{result.message}</p>
            {result.atoms.length > 0 ? <AtomTally atoms={result.atoms} /> : null}
          </FeedbackPanel>
        ) : null}
      </section>
    </div>
  );
}
